import { useState } from 'react';
import { ActionIcon, Group, Stack, Text, TextInput } from '@cowbell/ui/core';
import {
  useShellAgents,
  useShellDrawer,
  useShellSelection,
} from '../../provider';
import {
  useConversations,
  useDeleteConversation,
} from '../../provider/queries';
import { useMooChatContext } from '../../provider/internal-context';
import type { ConversationRef } from '../../types';
import { ConversationRow } from './ConversationRow';
import { ArrowLeftIcon } from './icons';

interface RecencyBuckets {
  today: ConversationRef[];
  yesterday: ConversationRef[];
  thisWeek: ConversationRef[];
  older: ConversationRef[];
}

function startOfDayMs(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function groupByRecency(
  refs: ConversationRef[],
  now: Date = new Date(),
): RecencyBuckets {
  const today = startOfDayMs(now);
  const yesterday = today - 24 * 60 * 60 * 1000;
  const sevenDaysAgo = today - 7 * 24 * 60 * 60 * 1000;
  const out: RecencyBuckets = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: [],
  };
  for (const ref of refs) {
    const t = Date.parse(ref.updatedAt || '') || 0;
    if (t >= today) {
      out.today.push(ref);
    } else if (t >= yesterday) {
      out.yesterday.push(ref);
    } else if (t >= sevenDaysAgo) {
      out.thisWeek.push(ref);
    } else {
      out.older.push(ref);
    }
  }
  return out;
}

function sortByRecency(a: ConversationRef, b: ConversationRef): number {
  return (b.updatedAt || '').localeCompare(a.updatedAt || '');
}

interface BucketProps {
  readonly label: string;
  readonly refs: ConversationRef[];
  readonly agentNameById: (id: string) => string;
  readonly onOpen: (ref: ConversationRef) => void;
  readonly onDelete: (ref: ConversationRef) => void;
}

function Bucket({ label, refs, agentNameById, onOpen, onDelete }: BucketProps) {
  if (refs.length === 0) return null;
  return (
    <Stack gap="xs">
      <Text size="xs" fw={700} c="dimmed">
        {label}
      </Text>
      {refs.map((c) => (
        <ConversationRow
          key={c.id}
          ref_={c}
          agentName={agentNameById(c.agentId)}
          onOpen={() => onOpen(c)}
          onDelete={() => onDelete(c)}
        />
      ))}
    </Stack>
  );
}

/** Expanded "all conversations" view, grouped by recency bucket. */
export function AllConversationsView() {
  useConversations();
  const { state } = useMooChatContext();
  const agents = useShellAgents();
  const drawer = useShellDrawer();
  const { openConversation } = useShellSelection();
  const deleteMutation = useDeleteConversation();
  const [search, setSearch] = useState('');

  const all = Object.values(state.conversations.byId)
    .sort(sortByRecency)
    .filter(
      (c) =>
        search === '' ||
        (c.title ?? '').toLowerCase().includes(search.toLowerCase()),
    );
  const buckets = groupByRecency(all);

  const agentNameById = (id: string) => agents.byId[id]?.name ?? '';
  const onOpen = (c: ConversationRef) => openConversation(c.id, c.agentId);
  const onDelete = (c: ConversationRef) => deleteMutation.mutate(c.id);

  return (
    <Stack gap="md" p="md">
      <Group gap="xs">
        <ActionIcon
          variant="subtle"
          aria-label="Back"
          onClick={() => drawer.setView('default')}
        >
          <ArrowLeftIcon />
        </ActionIcon>
        <Text fw={600}>All conversations</Text>
      </Group>
      <TextInput
        aria-label="Search conversations"
        placeholder="Search conversations…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        size="xs"
      />
      <Bucket
        label="Today"
        refs={buckets.today}
        agentNameById={agentNameById}
        onOpen={onOpen}
        onDelete={onDelete}
      />
      <Bucket
        label="Yesterday"
        refs={buckets.yesterday}
        agentNameById={agentNameById}
        onOpen={onOpen}
        onDelete={onDelete}
      />
      <Bucket
        label="This week"
        refs={buckets.thisWeek}
        agentNameById={agentNameById}
        onOpen={onOpen}
        onDelete={onDelete}
      />
      <Bucket
        label="Older"
        refs={buckets.older}
        agentNameById={agentNameById}
        onOpen={onOpen}
        onDelete={onDelete}
      />
      {Object.values(state.conversations.byId).length === 0 ? (
        <Text size="sm" c="dimmed">
          No conversations yet.
        </Text>
      ) : null}
      {Object.values(state.conversations.byId).length > 0 && all.length === 0 ? (
        <Text size="sm" c="dimmed">
          No conversations match your search.
        </Text>
      ) : null}
    </Stack>
  );
}
